if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.classList.contains('bst-product-gallery')) {
          this.initBstPagination();
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.initGalleryArrows();
      }

      initGalleryArrows() {
        this.viewerPrevButton = this.elements.viewer.querySelector('.slider-buttons button[name="previous"]');
        this.viewerNextButton = this.elements.viewer.querySelector('.slider-buttons button[name="next"]');
        if (!this.viewerPrevButton || !this.viewerNextButton) return;

        this.viewerPrevButton.addEventListener('click', () => this.stepGalleryArrow(-1));
        this.viewerNextButton.addEventListener('click', () => this.stepGalleryArrow(1));
        window.requestAnimationFrame(() => this.syncGalleryArrows());
      }

      stepGalleryArrow(direction) {
        const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = items.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
        const nextItem = items[activeIndex + direction];
        if (!nextItem) return;
        nextItem.querySelector('button').click();
      }

      syncGalleryArrows() {
        if (!this.viewerPrevButton || !this.viewerNextButton) return;
        const items = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = items.findIndex((item) => item.querySelector('button').hasAttribute('aria-current'));
        this.viewerPrevButton.toggleAttribute('disabled', activeIndex <= 0);
        this.viewerNextButton.toggleAttribute('disabled', activeIndex === -1 || activeIndex >= items.length - 1);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, preventPageScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          if (preventPageScroll) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.syncGalleryArrows();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initBstPagination() {
        this.bstPagination = this.querySelector('.bst-product-gallery__pagination');
        if (!this.bstPagination || !this.elements.viewer) return;

        this.bstBars = this.bstPagination.querySelectorAll('.bst-product-gallery__bar');
        this.elements.viewer.addEventListener('slideChanged', this.onBstSlideChanged.bind(this));

        this.bstBars.forEach((bar) => {
          bar.addEventListener('click', () => {
            const slideIndex = Number(bar.dataset.index);
            const slide = this.elements.viewer.sliderItemsToShow?.[slideIndex];
            if (slide && this.elements.viewer.setSlidePosition) {
              this.elements.viewer.setSlidePosition(slide.offsetLeft);
            }
          });
        });

        window.requestAnimationFrame(() => this.syncBstPagination());
      }

      onBstSlideChanged(event) {
        this.syncBstPagination(event.detail.currentElement);
      }

      syncBstPagination(activeElement) {
        if (!this.bstBars?.length || !this.elements.viewer?.sliderItemsToShow?.length) return;

        const currentElement =
          activeElement ||
          this.elements.viewer.sliderItemsToShow[Math.max(0, (this.elements.viewer.currentPage || 1) - 1)];
        const activeIndex = Array.from(this.elements.viewer.sliderItemsToShow).indexOf(currentElement);

        this.bstBars.forEach((bar, index) => {
          const isActive = index === activeIndex;
          bar.classList.toggle('is-active', isActive);
          bar.setAttribute('aria-selected', isActive);
        });
      }
    }
  );
}
